import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Upload, FileText, Sparkles, CheckCircle2, AlertCircle, Loader2, X, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const MAX_FILES = 25;
const MAX_BYTES = 50 * 1024 * 1024;

const STEP_LABEL = {
    queued: "Queued",
    uploading: "Uploading PDF",
    extracting: "Extracting text",
    "ai-formatting": "AI is formatting the lesson",
    "rendering-pages": "Rendering page images",
    done: "Done",
    failed: "Failed",
};

const QUIZ_LABEL = {
    pending: "Quiz queued — will generate after lessons finish",
    generating: "AI is writing a 20-question chapter quiz",
    done: "Chapter quiz ready",
    skipped: "Quiz skipped",
    failed: "Quiz generation failed",
};

function fmtSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function BulkImportPdfDialog({ open, onOpenChange, chapterId, chapterTitle, onDone }) {
    const [files, setFiles] = useState([]);
    const [autoQuiz, setAutoQuiz] = useState(true);
    const [overwrite, setOverwrite] = useState(false);
    const [job, setJob] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);
    const pollRef = useRef(null);

    useEffect(() => {
        if (open) {
            setFiles([]);
            setAutoQuiz(true);
            setOverwrite(false);
            setJob(null);
            setSubmitting(false);
        }
    }, [open]);

    // Poll job status
    useEffect(() => {
        if (!job?.id || job.status === "complete" || job.status === "failed") {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return undefined;
        }
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/lesson-import-jobs/${job.id}`);
                setJob(data);
                if (data.status === "complete" || data.status === "failed") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    if (data.status === "complete") {
                        const ok = (data.files || []).filter((f) => f.status === "done").length;
                        const fail = (data.files || []).filter((f) => f.status === "failed").length;
                        const qStatus = data.quiz_status;
                        const qCount = data.quiz_question_count || 0;
                        let quizMsg = "";
                        if (qStatus === "done" && qCount) quizMsg = ` · ${qCount}-Q quiz generated`;
                        else if (qStatus === "skipped") quizMsg = " · quiz skipped";
                        else if (qStatus === "failed") quizMsg = " · quiz failed";
                        toast.success(`Import done — ${ok} lesson${ok === 1 ? "" : "s"} created${fail ? `, ${fail} failed` : ""}${quizMsg}.`);
                        onDone?.();
                    }
                }
            } catch { /* ignore */ }
        }, 1500);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
        };
    }, [job?.id, job?.status, onDone]);

    const addFiles = (incoming) => {
        const arr = Array.from(incoming || []);
        const pdfs = arr.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
        const ok = [];
        for (const f of pdfs) {
            if (f.size > MAX_BYTES) {
                toast.error(`${f.name} is too large (max ${fmtSize(MAX_BYTES)}).`);
                continue;
            }
            ok.push(f);
        }
        const merged = [...files, ...ok].slice(0, MAX_FILES);
        if (files.length + ok.length > MAX_FILES) {
            toast.message(`You can import up to ${MAX_FILES} PDFs at a time.`);
        }
        setFiles(merged);
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addFiles(e.dataTransfer.files);
    };

    const submit = async () => {
        if (files.length === 0) {
            toast.error("Add at least one PDF first.");
            return;
        }
        setSubmitting(true);

        // Upload one file per HTTP request so we never hit the Cloudflare /
        // ingress body-size ceiling (~100 MB) on big chapters. We only ask
        // the FIRST batch to run the quiz generation — subsequent batches
        // just append lessons; the backend is smart enough to regenerate
        // the quiz if needed on the next explicit AI-Draft click.
        setJob({
            id: null,
            status: "running",
            mode: "attachment",
            total: files.length,
            current_index: 0,
            files: files.map((f) => ({
                filename: f.name,
                status: "queued",
                error: null,
                lesson_id: null,
            })),
            skipped_duplicates: [],
            replaced: [],
            quiz_status: autoQuiz ? "pending" : "skipped",
            quiz_question_count: 0,
            quiz_error: null,
        });

        const jobIds = [];
        const skippedAll = [];
        const replacedAll = [];
        let createdCount = 0;
        let failedCount = 0;

        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            // Update the inline progress view before the request starts so
            // the user sees "Uploading 3/12 — Lesson 3 Collaborating.pdf".
            setJob((prev) =>
                prev
                    ? {
                          ...prev,
                          current_index: i,
                          files: prev.files.map((fi, idx) =>
                              idx === i ? { ...fi, status: "uploading" } : fi,
                          ),
                      }
                    : prev,
            );

            try {
                const fd = new FormData();
                fd.append("files", f);
                // Only run the AI quiz pass on the last file so we don't
                // waste tokens generating a partial quiz on each request.
                fd.append(
                    "auto_quiz",
                    String(autoQuiz && i === files.length - 1),
                );
                fd.append("overwrite", String(overwrite));

                const { data } = await api.post(
                    `/chapters/${chapterId}/lessons/import-pdfs`,
                    fd,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        // 2 min per single file — plenty for 60 MB over slow wifi.
                        timeout: 2 * 60 * 1000,
                    },
                );

                if (data.job_id) {
                    jobIds.push(data.job_id);
                    // Wait for this per-file job to finish before starting
                    // the next so we don't hammer the storage backend with
                    // parallel writes.
                    const status = await waitForJob(data.job_id);
                    if (status?.files?.[0]?.status === "done") {
                        createdCount += 1;
                        setJob((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      files: prev.files.map((fi, idx) =>
                                          idx === i
                                              ? {
                                                    ...fi,
                                                    status: "done",
                                                    lesson_id:
                                                        status.files[0].lesson_id,
                                                }
                                              : fi,
                                      ),
                                  }
                                : prev,
                        );
                    } else {
                        failedCount += 1;
                        setJob((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      files: prev.files.map((fi, idx) =>
                                          idx === i
                                              ? {
                                                    ...fi,
                                                    status: "failed",
                                                    error:
                                                        status?.files?.[0]?.error
                                                        || "Upload failed",
                                                }
                                              : fi,
                                      ),
                                  }
                                : prev,
                        );
                    }
                }
                (data.skipped_duplicates || []).forEach((n) => skippedAll.push(n));
                (data.replaced || []).forEach((n) => replacedAll.push(n));
                if (!data.job_id && !(data.skipped_duplicates || []).length) {
                    failedCount += 1;
                }
                if ((data.skipped_duplicates || []).length) {
                    setJob((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  files: prev.files.map((fi, idx) =>
                                      idx === i
                                          ? {
                                                ...fi,
                                                status: "skipped",
                                                error: "Already in this chapter",
                                            }
                                          : fi,
                                  ),
                              }
                            : prev,
                    );
                }
            } catch (e) {
                failedCount += 1;
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                let msg = detail || "Upload failed.";
                if (!detail) {
                    if (
                        status === 413
                        || status === 502
                        || status === 504
                        || e?.code === "ECONNABORTED"
                    ) {
                        msg = `"${f.name}" is too large or the connection stalled — try compressing it below 20 MB (ilovepdf.com / Adobe 'Reduce File Size').`;
                    } else if (status) {
                        msg = `HTTP ${status}`;
                    }
                }
                setJob((prev) =>
                    prev
                        ? {
                              ...prev,
                              files: prev.files.map((fi, idx) =>
                                  idx === i
                                      ? { ...fi, status: "failed", error: msg }
                                      : fi,
                              ),
                          }
                        : prev,
                );
            }
        }

        // All uploads complete — flip to "complete" so the dialog footer
        // renders the "Import more" / "Done" buttons. Use the last job's id
        // so the status poller stops gracefully.
        setJob((prev) =>
            prev
                ? {
                      ...prev,
                      id: jobIds[jobIds.length - 1] || null,
                      status: "complete",
                      current_index: files.length,
                      skipped_duplicates: skippedAll,
                      replaced: replacedAll,
                  }
                : prev,
        );

        const totalOk = createdCount;
        const totalFail = failedCount;
        const totalSkip = skippedAll.length;
        const totalRepl = replacedAll.length;
        if (totalOk === 0 && totalSkip === files.length) {
            toast.message(
                "All uploaded PDFs already exist as lessons — tick 'Replace existing' if you want to overwrite them.",
                { duration: 6000 },
            );
        } else {
            const parts = [`${totalOk} lesson${totalOk === 1 ? "" : "s"} created`];
            if (totalRepl) parts.push(`${totalRepl} replaced`);
            if (totalSkip) parts.push(`${totalSkip} skipped`);
            if (totalFail) parts.push(`${totalFail} failed`);
            toast.success(`Import done — ${parts.join(", ")}.`);
        }
        onDone?.();
        setSubmitting(false);
    };

    // Poll a single-file import job until it reaches a terminal state.
    const waitForJob = async (jobId) => {
        for (let attempt = 0; attempt < 90; attempt++) {
            // 90 × 2s = 3 min ceiling per file (OCR / quiz / conversion time).
            try {
                const { data } = await api.get(`/lesson-import-jobs/${jobId}`);
                if (data.status === "complete" || data.status === "failed") {
                    return data;
                }
            } catch {
                /* transient — keep polling */
            }
            await new Promise((r) => setTimeout(r, 2000));
        }
        return null;
    };

    const reset = () => {
        setJob(null);
        setFiles([]);
    };

    const total = job?.total || files.length;
    const doneCount = (job?.files || []).filter(
        (f) => f.status === "done" || f.status === "failed",
    ).length;
    const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[92vh] overflow-y-auto"
                data-testid="bulk-import-pdf-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Bulk import lessons from PDFs
                    </DialogTitle>
                    <DialogDescription>
                        Drop in a chapter folder&rsquo;s worth of lesson PDFs.{" "}
                        {chapterTitle ? (
                            <>
                                Each becomes a new lesson under <strong>{chapterTitle}</strong>{" "}
                                with the original PDF attached for download.
                            </>
                        ) : (
                            <>Each becomes a new lesson with the original PDF attached.</>
                        )}{" "}
                        Done in seconds &mdash; convert individual lessons to readable text or page images later.
                    </DialogDescription>
                </DialogHeader>

                {!job ? (
                    <>
                        <div
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onDrop={onDrop}
                            onClick={() => inputRef.current?.click()}
                            className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer p-8 text-center"
                            data-testid="bulk-import-dropzone"
                        >
                            <Upload className="h-10 w-10 text-primary mx-auto mb-2" />
                            <div className="font-medium text-foreground">
                                Click or drop PDFs here
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                Up to {MAX_FILES} files · {fmtSize(MAX_BYTES)} each · scanned books OK
                            </div>
                            <input
                                ref={inputRef}
                                type="file"
                                multiple
                                accept="application/pdf,.pdf"
                                className="hidden"
                                onChange={(e) => addFiles(e.target.files)}
                                data-testid="bulk-import-file-input"
                            />
                        </div>

                        {files.length > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between mt-2">
                                    <Label className="text-xs uppercase tracking-wider">
                                        {files.length} PDF{files.length === 1 ? "" : "s"} ready
                                    </Label>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setFiles([])}
                                        className="h-7 text-xs gap-1"
                                        data-testid="bulk-import-clear-all"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                        Clear
                                    </Button>
                                </div>
                                <ScrollArea className="max-h-[280px] rounded-md border border-border">
                                    {files.map((f, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between px-3 py-2 border-b border-border last:border-b-0 text-sm"
                                            data-testid={`bulk-import-file-row-${idx}`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[10px] text-muted-foreground tabular-nums w-5 text-right flex-shrink-0">
                                                    {idx + 1}.
                                                </span>
                                                <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                                                <span className="truncate">{f.name}</span>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                <span className="text-xs text-muted-foreground">
                                                    {fmtSize(f.size)}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="text-muted-foreground hover:text-destructive"
                                                    onClick={() =>
                                                        setFiles(files.filter((_, i) => i !== idx))
                                                    }
                                                    aria-label="Remove"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </ScrollArea>
                                {files.length > 5 ? (
                                    <p
                                        className="text-[11px] text-muted-foreground italic mt-1 px-1"
                                        data-testid="bulk-import-scroll-hint"
                                    >
                                        Showing all {files.length} files — scroll the list above to review the rest.
                                    </p>
                                ) : null}
                            </div>
                        )}

                        <div className="space-y-3 pt-2">
                            <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground leading-relaxed">
                                <div className="font-medium text-foreground mb-1">
                                    How this works
                                </div>
                                Each PDF becomes a lesson with the original PDF attached for download. We&rsquo;ll also auto-generate a <strong>20-question chapter quiz</strong> from the digital text. Once imported, you can convert any individual lesson into <strong>readable text</strong> (Faithful / Improved) or <strong>page images</strong> (Replica) using the &ldquo;Convert PDF&rdquo; action on the lesson row.
                            </div>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="auto-quiz"
                                    checked={autoQuiz}
                                    onCheckedChange={(v) => setAutoQuiz(!!v)}
                                    data-testid="auto-quiz-checkbox"
                                />
                                <Label htmlFor="auto-quiz" className="cursor-pointer font-normal text-sm">
                                    Auto-generate a 20-question chapter quiz from the imported lessons
                                    <span className="block text-xs text-muted-foreground">
                                        Skipped if this chapter already has a quiz, or if the PDFs are fully scanned without digital text.
                                    </span>
                                </Label>
                            </div>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="overwrite-duplicates"
                                    checked={overwrite}
                                    onCheckedChange={(v) => setOverwrite(!!v)}
                                    data-testid="overwrite-duplicates-checkbox"
                                />
                                <Label htmlFor="overwrite-duplicates" className="cursor-pointer font-normal text-sm">
                                    Replace existing lessons with the same title
                                    <span className="block text-xs text-muted-foreground">
                                        Use this if a previous upload was interrupted and you want to redo it. Existing lessons with matching names will be deleted first.
                                    </span>
                                </Label>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                data-testid="bulk-import-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={submit}
                                disabled={submitting || files.length === 0}
                                className="bg-primary hover:bg-primary/90 gap-1.5"
                                data-testid="bulk-import-start"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Starting&hellip;
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="h-4 w-4" />
                                        Import {files.length} PDF{files.length === 1 ? "" : "s"}
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <ProgressPanel job={job} pct={pct} doneCount={doneCount} total={total} onReset={reset} onClose={() => onOpenChange(false)} />
                )}
            </DialogContent>
        </Dialog>
    );
}

function ProgressPanel({ job, pct, doneCount, total, onReset, onClose }) {
    const finished = job.status === "complete" || job.status === "failed";
    return (
        <div className="space-y-4" data-testid="bulk-import-progress-panel">
            <div>
                <div className="flex items-center justify-between mb-1.5">
                    <div className="text-sm font-medium">
                        {finished
                            ? "Import finished"
                            : `Processing ${doneCount + 1} / ${total}…`}
                    </div>
                    <div className="text-xs text-muted-foreground">{pct}%</div>
                </div>
                <Progress value={pct} className="h-2" />
            </div>

            <ScrollArea className="max-h-[50vh] rounded-md border border-border">
                {(job.files || []).map((f, idx) => (
                    <FileRow key={idx} f={f} idx={idx} />
                ))}
                {job.quiz_status && job.quiz_status !== "skipped" ? (
                    <QuizRow
                        status={job.quiz_status}
                        count={job.quiz_question_count}
                        error={job.quiz_error}
                    />
                ) : null}
            </ScrollArea>

            <DialogFooter>
                {finished ? (
                    <>
                        <Button variant="outline" onClick={onReset} data-testid="bulk-import-import-more">
                            Import more
                        </Button>
                        <Button onClick={onClose} className="bg-primary hover:bg-primary/90" data-testid="bulk-import-done">
                            Done
                        </Button>
                    </>
                ) : (
                    <Button variant="outline" disabled className="cursor-not-allowed">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Working in the background…
                    </Button>
                )}
            </DialogFooter>
        </div>
    );
}

function FileRow({ f, idx }) {
    let icon = <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    let bg = "bg-card";
    if (f.status === "done") {
        icon = <CheckCircle2 className="h-4 w-4 text-primary" />;
        bg = "bg-primary/5";
    } else if (f.status === "failed") {
        icon = <AlertCircle className="h-4 w-4 text-destructive" />;
        bg = "bg-destructive/5";
    } else if (f.status === "queued") {
        icon = <FileText className="h-4 w-4 text-muted-foreground" />;
    }

    let statusLine = STEP_LABEL[f.status] || f.status;
    if (f.status === "rendering-pages" && f.render_progress?.total) {
        statusLine = `Rendering page ${f.render_progress.done} / ${f.render_progress.total}`;
    } else if (f.status === "done" && f.pages) {
        statusLine += ` · ${f.pages}p${f.images ? ` · ${f.images} image${f.images === 1 ? "" : "s"}` : ""}${f.ocr_pages ? ` · ${f.ocr_pages} OCR'd` : ""}`;
    }

    return (
        <div
            className={`flex items-start gap-3 px-3 py-2 border-b border-border last:border-b-0 ${bg}`}
            data-testid={`progress-file-${idx}`}
        >
            <div className="mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.lesson_title || f.filename}</div>
                <div className="text-xs text-muted-foreground">{statusLine}</div>
                {f.error ? (
                    <div className="text-xs text-destructive mt-1 truncate" title={f.error}>
                        {f.error}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function QuizRow({ status, count, error }) {
    let icon = <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    let bg = "bg-card";
    if (status === "done") {
        icon = <CheckCircle2 className="h-4 w-4 text-primary" />;
        bg = "bg-primary/5";
    } else if (status === "failed") {
        icon = <AlertCircle className="h-4 w-4 text-destructive" />;
        bg = "bg-destructive/5";
    } else if (status === "pending") {
        icon = <Sparkles className="h-4 w-4 text-muted-foreground" />;
    }
    return (
        <div
            className={`flex items-start gap-3 px-3 py-2 border-b border-border last:border-b-0 ${bg}`}
            data-testid="bulk-import-quiz-row"
        >
            <div className="mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">Chapter quiz</div>
                <div className="text-xs text-muted-foreground">
                    {QUIZ_LABEL[status] || status}
                    {status === "done" && count ? ` · ${count} questions` : ""}
                </div>
                {error ? (
                    <div className="text-xs text-destructive mt-1 truncate" title={error}>
                        {error}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

